import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { HiMail, HiShieldCheck, HiClock, HiArrowLeft, HiSparkles, HiEye, HiEyeOff } from 'react-icons/hi';
import { API_URL } from '../../config/apiConfig';
import { getFcmToken } from '../../firebase/getFcmToken';

const VendorLogin = () => {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  // For development/debugging: server may return the OTP in response when not in production
  const [devOtp, setDevOtp] = useState(null);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/vendor/login/send-otp`, {
        email: email.trim()
      });

      if (response.data.status) {
        // Capture debug OTP if the backend returned it (available in non-production environments)
        const debugOtp = response.data?.data?.debugOtp || response.data?.debugOtp || null;
        if (debugOtp) {
          setDevOtp(String(debugOtp));
        } else {
          setDevOtp(null);
        }
        setStep(2);
        setOtpExpiresAt(new Date(Date.now() + 5 * 60 * 1000)); // 5 minutes from now
      } else {
        toast.error(response.data.message || 'Failed to send OTP');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send OTP';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim() || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      // Attempt to get FCM token and include it in verify payload
      let fcmToken = null;
      try {
        fcmToken = await getFcmToken();
      } catch (err) {
        console.warn('Failed to get FCM token for vendor login:', err);
      }
      const response = await axios.post(`${API_URL}/vendor/login/verify-otp`, {
        email: email.trim(),
        otp: otp.trim(),
        fcmToken
      });

      if (response.data.status) {
        const { data, token, mustSetPassword } = response.data;

        // Store vendor data and token
        if (token) {
          localStorage.setItem('vendorToken', token);
        }
        if (data) {
          localStorage.setItem('vendorData', JSON.stringify(data));
        }

        if (mustSetPassword) {
          // Mark local flag so set-password page knows OTP was just verified and vendor is authenticated
          localStorage.setItem('vendorMustSetPassword', 'true');
          // Navigate to set password page for first-time vendors
          navigate('/vendor/set-password');
        } else {
          navigate('/vendor/dashboard');
        }
      } else {
        toast.error(response.data.message || 'Invalid OTP');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Invalid OTP';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Password-based login handler (for vendors who set a password)
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      let fcmToken = null;
      try {
        fcmToken = await getFcmToken();
      } catch (err) {
        console.warn('Failed to get FCM token for vendor password login:', err);
      }

      const res = await axios.post(`${API_URL}/vendor/auth/login`, { 
        email: email.trim(), 
        password,
        fcmToken 
      });
      if (res.data && (res.data.status || res.data.success)) {
        const { data, token } = res.data;
        if (token) localStorage.setItem('vendorToken', token);
        if (data) localStorage.setItem('vendorData', JSON.stringify(data));
        navigate('/vendor/dashboard');
      } else {
        toast.error(res.data?.message || 'Login failed');
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      const response = await axios.post(`${API_URL}/vendor/login/send-otp`, {
        email: email.trim()
      });

      if (response.data.status) {
        // Capture debug OTP if returned by backend
        const debugOtp = response.data?.data?.debugOtp || response.data?.debugOtp || null;
        if (debugOtp) {
          setDevOtp(String(debugOtp));
        } else {
          setDevOtp(null);
        }
        setOtpExpiresAt(new Date(Date.now() + 5 * 60 * 1000));
        setOtp('');
      } else {
        toast.error(response.data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to resend OTP';
      toast.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const [timeLeft, setTimeLeft] = React.useState(0);

  React.useEffect(() => {
    if (otpExpiresAt) {
      const timer = setInterval(() => {
        const now = new Date();
        const diff = Math.max(0, Math.floor((otpExpiresAt - now) / 1000));
        setTimeLeft(diff);

        if (diff === 0) {
          clearInterval(timer);
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [otpExpiresAt]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-white/30 backdrop-blur-3xl">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-indigo-100/20 to-purple-100/20"></div>
      </div>

      <div className="relative z-10 sm:mx-auto sm:w-full sm:max-w-md">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-20 w-20 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-xl">
            <HiSparkles className="h-10 w-10 text-white" />
          </div>
          <h2 className="mt-6 text-4xl font-bold bg-gradient-to-r from-gray-900 via-indigo-900 to-purple-900 bg-clip-text text-transparent">
            CabZigo
          </h2>
          <p className="mt-2 text-lg font-medium text-indigo-600">Vendor Portal</p>
          {/* <div className="mt-4 h-1 w-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full mx-auto"></div>
          <div className="mt-4 text-center">
            <Link to="/vendor/forgot-password/send-otp" className="text-sm text-indigo-600 hover:underline">Forgot password?</Link>
          </div> */}
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-white/80 backdrop-blur-lg py-10 px-8 shadow-2xl sm:rounded-3xl border border-white/20">
          {step === 1 ? (
            <div>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900">
                  Welcome Back
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Enter your email to access your vendor dashboard
                </p>
              </div>

              {!showPasswordLogin ? (
                <form onSubmit={handleSendOtp} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <HiMail className="h-5 w-5 text-indigo-400" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50/50"
                        placeholder="Enter your registered email"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-4 px-6 border border-transparent rounded-2xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                        Sending OTP...
                      </div>
                    ) : (
                      <>
                        <HiMail className="h-5 w-5 mr-2" />
                        Send Verification Code
                      </>
                    )}
                  </button>

                  <div className="mt-4 flex justify-center">
                    <button type="button" onClick={() => setShowPasswordLogin(true)} className="text-sm text-indigo-600 underline">Sign in with password</button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handlePasswordLogin} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                    <input id="email" name="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="block w-full border rounded p-2" placeholder="Enter your registered email" />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                    <div className="relative">
                      <input id="password" name="password" type={showPassword ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} className="block w-full border rounded p-2 pr-10" placeholder="Enter your password" />
                      <button type="button" onClick={() => setShowPassword(prev => !prev)} className="absolute right-2 top-2 text-gray-500 hover:text-gray-700 focus:outline-none">
                        {showPassword ? <HiEyeOff /> : <HiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button type="submit" disabled={loading} className={`px-4 py-2 rounded ${loading ? 'bg-gray-400' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
                      {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                  </div>

                  <div className="mt-4 text-center text-sm">
                    <button type="button" onClick={() => setShowPasswordLogin(false)} className="font-medium text-indigo-600 hover:text-indigo-500">Back to OTP login</button>
                  </div>
                </form>
              )}

              <div className="mt-8">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500 font-medium">
                      New to CabZigo?
                    </span>
                  </div>
                </div>

                <div className="mt-6">
                  <button
                    onClick={() => navigate('/vendor/register')}
                    className="w-full flex justify-center py-4 px-6 border-2 border-indigo-200 rounded-2xl shadow-sm text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                  >
                    Register as Vendor Partner
                  </button>
                </div>

                <div className="mt-4 text-center text-sm">
                  <Link to="/vendor/forgot-password/send-otp" className="font-medium text-indigo-600 hover:text-indigo-500">
                    Forgot your password?
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-center mb-8">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center mb-4">
                  <HiShieldCheck className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Verify Your Email
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  We've sent a 6-digit code to<br />
                  <span className="font-semibold text-indigo-600">{email}</span>
                </p>
              </div>

              <form onSubmit={handleVerifyOtp} className="space-y-6">
                <div>
                  <label htmlFor="otp" className="block text-sm font-semibold text-gray-700 mb-2">
                    Verification Code
                  </label>
                  <div className="relative">
                    <input
                      id="otp"
                      name="otp"
                      type="text"
                      maxLength="6"
                      pattern="[0-9]{6}"
                      required
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      className="block w-full px-4 py-4 border-2 border-gray-200 rounded-2xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-center text-2xl tracking-widest font-bold bg-gray-50/50"
                      placeholder="000000"
                      autoComplete="one-time-code"
                    />
                  </div>
                  {timeLeft > 0 && (
                    <div className="mt-3 text-center">
                      <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        <HiClock className="h-4 w-4 mr-1" />
                        Expires in {formatTime(timeLeft)}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-4 px-6 border border-transparent rounded-2xl shadow-lg text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                      Verifying...
                    </div>
                  ) : (
                    <>
                      <HiShieldCheck className="h-5 w-5 mr-2" />
                      Verify & Continue
                    </>
                  )}
                </button>

                {devOtp && (
                  <div className="mt-4 text-center">
                    <div className="text-sm text-gray-600">Actual OTP (displayed for debugging):</div>
                    <div className="mt-2 inline-flex items-center px-4 py-2 rounded-lg bg-green-50 text-green-900 border border-green-100 text-2xl font-bold tracking-widest">
                      {devOtp}
                    </div>
                  </div>
                )}

              </form>

              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200"
                >
                  <HiArrowLeft className="h-4 w-4 mr-1" />
                  Change email
                </button>
                <button
                  onClick={handleResendOtp}
                  disabled={resendLoading || timeLeft > 0}
                  className="text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors duration-200"
                >
                  {resendLoading ? 'Sending...' : 'Resend Code'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorLogin;
